import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";
import Reveal from "@/components/common/Reveal";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function PortalSignupPage() {
    const { signup } = useAuth();
    const navigate = useNavigate();

    const [form, setForm] = useState({
        full_name: "",
        email: "",
        password: "",
        company: "",
    });
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);

    const update = (k) => (e) => {
        setForm((f) => ({ ...f, [k]: e.target.value }));
        if (errors[k]) setErrors((prev) => ({ ...prev, [k]: null }));
    };

    const submit = async (e) => {
        e.preventDefault();
        const next = {};
        if (form.full_name.trim().length < 2) next.full_name = "Enter your full name.";
        if (!EMAIL_RE.test(form.email)) next.email = "Enter a valid email.";
        if (form.password.length < 8) next.password = "Password must be at least 8 characters.";
        setErrors(next);
        if (Object.keys(next).length) return;

        setSubmitting(true);
        try {
            await signup({
                full_name: form.full_name.trim(),
                email: form.email.trim().toLowerCase(),
                password: form.password,
                company: form.company.trim() || null,
            });
            toast.success("Welcome to the ElevateBuild Training Portal.");
            navigate("/portal/dashboard", { replace: true });
        } catch (err) {
            const msg = formatApiErrorDetail(err?.response?.data?.detail) || err.message;
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section
            className="grid min-h-[calc(100vh-72px)] bg-brand-charcoal md:grid-cols-2"
            data-testid="portal-signup-page"
        >
            <div className="relative hidden md:block">
                <img
                    src="https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&w=1400&q=80"
                    alt=""
                    className="h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-brand-charcoal/70 via-brand-charcoal/40 to-brand-charcoal/80" />
                <div className="absolute inset-0 flex flex-col justify-end p-12 text-brand-offwhite">
                    <p className="font-mono-tag text-brand-gold">
                        [ TRAINING PORTAL ]
                    </p>
                    <h1 className="mt-4 font-display text-6xl leading-[1.05]">
                        START <br />
                        <span className="text-brand-gold">HERE.</span>
                    </h1>
                </div>
            </div>

            <div className="grid place-items-center bg-brand-offwhite p-8 md:p-16">
                <Reveal className="w-full max-w-md">
                    <p className="font-mono-tag text-brand-gold">[ CREATE ACCOUNT ]</p>
                    <h2 className="mt-4 font-display text-5xl leading-[1.1]">
                        REGISTER
                    </h2>
                    <form onSubmit={submit} noValidate className="mt-10" data-testid="signup-form">
                        <div>
                            <label className="form-label" htmlFor="signup-name">
                                Full name
                            </label>
                            <input
                                id="signup-name"
                                type="text"
                                value={form.full_name}
                                onChange={update("full_name")}
                                autoComplete="name"
                                className="form-input"
                                data-testid="signup-full-name"
                            />
                            {errors.full_name && (
                                <p className="mt-1 font-mono-tag text-destructive" data-testid="signup-name-error">
                                    {errors.full_name}
                                </p>
                            )}
                        </div>
                        <div className="mt-6">
                            <label className="form-label" htmlFor="signup-email">
                                Email
                            </label>
                            <input
                                id="signup-email"
                                type="email"
                                value={form.email}
                                onChange={update("email")}
                                autoComplete="email"
                                className="form-input"
                                data-testid="signup-email"
                            />
                            {errors.email && (
                                <p className="mt-1 font-mono-tag text-destructive" data-testid="signup-email-error">
                                    {errors.email}
                                </p>
                            )}
                        </div>
                        <div className="mt-6">
                            <label className="form-label" htmlFor="signup-password">
                                Password (min 8 characters)
                            </label>
                            <input
                                id="signup-password"
                                type="password"
                                value={form.password}
                                onChange={update("password")}
                                autoComplete="new-password"
                                className="form-input"
                                data-testid="signup-password"
                            />
                            {errors.password && (
                                <p className="mt-1 font-mono-tag text-destructive" data-testid="signup-password-error">
                                    {errors.password}
                                </p>
                            )}
                        </div>
                        <div className="mt-6">
                            <label className="form-label" htmlFor="signup-company">
                                Company (optional)
                            </label>
                            <input
                                id="signup-company"
                                type="text"
                                value={form.company}
                                onChange={update("company")}
                                autoComplete="organization"
                                className="form-input"
                                data-testid="signup-company"
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={submitting}
                            className="btn-primary mt-10 w-full disabled:cursor-not-allowed disabled:opacity-60"
                            data-testid="signup-submit"
                        >
                            {submitting ? "Creating account…" : "Create account"}
                            <ArrowUpRight className="h-4 w-4" />
                        </button>
                        <p className="mt-6 font-body text-sm text-brand-steel">
                            Already registered?{" "}
                            <Link
                                to="/portal/login"
                                className="text-brand-gold underline-offset-4 hover:underline"
                                data-testid="signup-login-link"
                            >
                                Log in
                            </Link>
                        </p>
                    </form>
                </Reveal>
            </div>
        </section>
    );
}
